const textHelpers = require("./text");

const TLDR_LABEL = /^tl\s*[:;]?\s*dr\s*:?\s*$/i;
const TLDR_PREFIX = /^(\s*tl\s*[:;]?\s*dr\s*:?\s*)([\s\S]*)$/i;

const PERMALINK_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>';

function emptyArticle(bodyHtml) {
  return {
    introHtml: "",
    tldrHtml: "",
    bodyHtml: bodyHtml || "",
    headings: [],
  };
}

function plainText(node) {
  return (node.textContent || "").replace(/\s+/g, " ").trim();
}

function headingLevel(element) {
  const match = /^H([1-6])$/.exec(element.tagName || "");
  return match ? Number(match[1]) : 0;
}

function slugify(text) {
  const slug = textHelpers.stringToSlug(text).replace(/^-+|-+$/g, "");
  return slug || "section";
}

function uniqueSlug(base, used) {
  let slug = base;
  let count = 2;
  while (used.has(slug)) {
    slug = `${base}-${count}`;
    count += 1;
  }
  used.add(slug);
  return slug;
}

function serialize(nodes) {
  return nodes
    .map((node) => {
      if (node.nodeType === 1) {
        return node.outerHTML;
      }
      if (node.nodeType === 3) {
        return node.textContent;
      }
      return "";
    })
    .join("")
    .trim();
}

function isTldrHeading(element) {
  return headingLevel(element) > 0 && TLDR_LABEL.test(plainText(element));
}

function isTldrParagraph(element) {
  return element.tagName === "P" && TLDR_PREFIX.test(plainText(element));
}

function isImageOnlyParagraph(element) {
  return (
    element.nodeType === 1 &&
    element.tagName === "P" &&
    !plainText(element) &&
    element.querySelector("img")
  );
}

function pruneEmpty(element) {
  [...element.children].forEach((child) => {
    pruneEmpty(child);
    if (!plainText(child) && !child.querySelector("img, br, iframe, video")) {
      child.remove();
    }
  });
}

function stripLeadingTldr(element) {
  let pending = true;

  function walk(node) {
    if (!pending) {
      return;
    }
    if (node.nodeType === 3) {
      if (!node.textContent.trim()) {
        return;
      }
      const match = node.textContent.match(TLDR_PREFIX);
      if (match) {
        node.textContent = match[2];
        pending = false;
        return;
      }
      pending = false;
      return;
    }
    if (node.nodeType !== 1) {
      return;
    }
    [...node.childNodes].some((child) => {
      walk(child);
      return !pending;
    });
  }

  walk(element);
  pruneEmpty(element);
}

function takeUntilNextHeading(start, level) {
  const section = [];
  const media = [];
  let sibling = start.nextSibling;

  while (sibling) {
    const next = sibling.nextSibling;
    const levelOfSibling = sibling.nodeType === 1 ? headingLevel(sibling) : 0;
    if (levelOfSibling && levelOfSibling <= level) {
      break;
    }
    if (sibling.nodeType === 1 && isImageOnlyParagraph(sibling)) {
      media.push(sibling);
    } else if (
      sibling.nodeType === 1 ||
      (sibling.nodeType === 3 && sibling.textContent.trim())
    ) {
      section.push(sibling);
    }
    sibling.remove();
    sibling = next;
  }

  return { section, media };
}

function extractTldr(root) {
  const candidates = [
    ...root.querySelectorAll("h1, h2, h3, h4, h5, h6, p"),
  ];

  for (let index = 0; index < candidates.length; index += 1) {
    const node = candidates[index];
    if (!node.parentNode) {
      continue;
    }
    if (isTldrHeading(node)) {
      const collected = takeUntilNextHeading(node, headingLevel(node));
      node.remove();
      return {
        html: serialize(collected.section),
        media: collected.media,
      };
    }
    if (isTldrParagraph(node)) {
      stripLeadingTldr(node);
      const html = node.innerHTML.trim();
      node.remove();
      return {
        html: html ? `<p>${html}</p>` : "",
        media: [],
      };
    }
  }

  return { html: "", media: [] };
}

function stampHeadings(root) {
  const headings = [];
  const used = new Set();

  root.querySelectorAll("h2, h3, h4").forEach((heading) => {
    const text = plainText(heading);
    if (!text || TLDR_LABEL.test(text)) {
      return;
    }
    if (heading.querySelector(".blog-heading-anchor")) {
      return;
    }

    const id = uniqueSlug(slugify(text), used);
    heading.id = id;

    const anchor = heading.ownerDocument.createElement("a");
    anchor.className = "blog-heading-anchor";
    anchor.href = `#${id}`;
    anchor.setAttribute("aria-label", "Link to this section");
    anchor.innerHTML = PERMALINK_ICON;
    heading.insertBefore(anchor, heading.firstChild);

    headings.push({
      id,
      text,
      level: headingLevel(heading),
    });
  });

  return headings;
}

function wrapTables(root) {
  root.querySelectorAll("table").forEach((table) => {
    if (table.parentElement.classList.contains("blog-table-scroll")) {
      return;
    }
    const wrapper = table.ownerDocument.createElement("div");
    wrapper.className = "blog-table-scroll";
    table.parentNode.insertBefore(wrapper, table);
    wrapper.appendChild(table);
  });
}

function splitIntro(root) {
  const intro = [];
  const rest = [];
  let seenHeading = false;

  [...root.childNodes].forEach((node) => {
    if (!seenHeading && node.nodeType === 1 && headingLevel(node) === 2) {
      seenHeading = true;
    }
    if (seenHeading) {
      rest.push(node);
    } else {
      intro.push(node);
    }
  });

  return { intro, rest };
}

function prepareBlogArticle(html) {
  if (!html) {
    return emptyArticle("");
  }
  if (typeof DOMParser === "undefined") {
    return emptyArticle(html);
  }

  const document = new DOMParser().parseFromString(
    `<div id="blog-article-root">${html}</div>`,
    "text/html"
  );
  const root = document.getElementById("blog-article-root");
  if (!root) {
    return emptyArticle(html);
  }

  wrapTables(root);
  const tldr = extractTldr(root);
  const headings = stampHeadings(root);
  const parts = splitIntro(root);

  return {
    introHtml: serialize(parts.intro),
    tldrHtml: tldr.html,
    bodyHtml: [serialize(tldr.media), serialize(parts.rest)]
      .filter(Boolean)
      .join(""),
    headings,
  };
}

module.exports = {
  prepareBlogArticle,
};
